import DateJS, { format2DateStr, format2TimeStamp } from '@/utils/date';
import getRandomId from '@/utils/uuid';
import { MemberType } from '@/config/constant';
import { parseFen2Yuan, parseYuan2Fen } from '@/utils/number';
import ReserveService from '@/api/reserve';
import {
  ExchangeRentRoomInfo,
  ExchangeRoomPriceInfo,
  ExchangeRoomRole,
  RoomExchangeType,
  RoomGuestItem,
} from '@/redux/reducers/reservation/base.d';
import { ReducerState, MainState } from '@/redux/reducers/reservation/checkInExchange';
import { findItemByKey, flatPriceList } from '@/redux/actions/reservation/helper';
import { createTargetGetter, TargetType } from '@/components/Reservation/PopupTarget';

export const getStateFromOrder: (recNo: string) => Promise<MainState> = async (recNo) => {
  const res = await ReserveService.getReserveExchangeDetail(recNo);
  const orderInfo = res.data;
  const {
    roomTypeId,
    roomTypeName,
    roomPriceList,
    roomId,
    roomNo,
    sourceChannelId,
    sourceChannelName,
    priceStrategyId,
    priceStrategyName,
    checkinTime,
    expectedDepartureTime,
    occDuration,
    resvNo,
    occInfoList,
    deposit,
    roomCardNum,
    // masterInfo,
    peripheralCount,
  } = orderInfo;
  let { masterInfo } = orderInfo;
  const originRoomPriceList: ExchangeRoomPriceInfo[] = [];
  const targetRoomPriceList: ExchangeRoomPriceInfo[] = [];
  (roomPriceList || []).forEach((item) => {
    const {
      rentDate,
      price,
      couponAmount,
      couponName,
      couponNo,
      couponType,
      couponWyuid,
      couponSubType,
      extensionId,
      occType,
      priceChannel,
    } = item;
    const date = format2DateStr(rentDate);
    const inputPrice = parseFen2Yuan(price);
    const couponValue = parseFen2Yuan(couponAmount);
    const itemPriceInfo = {
      rentDateId: getRandomId(),
      occType,
      rentDate: date,
      inputPrice,
      extensionId,
      couponAmount,
      couponName,
      couponNo,
      couponType,
      couponWyuid,
      couponSubType,
      couponValue,
      isBeforeAudit: false, // 默认都是未过夜审
      priceChannel,
    };
    originRoomPriceList.push({ ...itemPriceInfo });
    targetRoomPriceList.push({ ...itemPriceInfo });
  });
  const allGuestList: RoomGuestItem[] = [];
  (occInfoList || []).forEach((item) => {
    const { certType, certNo, mobile, memberType, name, memberLevel, balance } = item;
    const info: RoomGuestItem = {
      indexId: getRandomId(),
      roomNo: item.roomNo,
      name,
      memberType,
      certType,
      certNo,
      mobile,

      // memberFlag,
      memberLevel,
      remainingBalance: balance,
    };
    if (memberType === MemberType.HouseOwner) {
      masterInfo = item;
      // 房主
      allGuestList.unshift(info); // 房主在第一位
    } else {
      allGuestList.push(info);
    }
  });
  const roomDeposit = parseFen2Yuan(deposit); // 押金
  const rentRoomInfos: ExchangeRentRoomInfo[] = [
    {
      indexId: getRandomId(),
      itemRole: ExchangeRoomRole.OriginRoom,
      roomTypeId,
      roomTypeName,
      roomId,
      roomNo,
      deposit: roomDeposit,
      roomCardCount: roomCardNum || 0,
      roomPriceList: originRoomPriceList,
    },
    {
      indexId: getRandomId(),
      itemRole: ExchangeRoomRole.TargetRoom,
      roomTypeId,
      roomTypeName, // 默认同房型
      roomId: null, // 默认不选择房间
      roomNo: null,
      deposit: roomDeposit,
      roomPriceList: targetRoomPriceList,
    },
  ];
  return {
    commonInfo: {
      exchangeType: RoomExchangeType.KeepSame,
      mainOrderId: resvNo,
      recNo,
      ownerWYId: masterInfo?.wyuId || orderInfo.wyuId,
      memberLevel: masterInfo?.memberLevel || orderInfo.memberLevel,
    },
    channelInfo: {
      channelId: sourceChannelId,
      channelName: sourceChannelName,
      strategyId: priceStrategyId,
      strategyName: priceStrategyName,
    },
    allGuestList,
    rentTimeInfo: {
      occType: orderInfo.occType,
      expectedArrivalTime: DateJS(checkinTime), // 入住时间
      businessDate: DateJS(originRoomPriceList[0].rentDate),
      expectedDepartureTime: DateJS(expectedDepartureTime),
      rentTime: occDuration,
      lastExtensionOccType: orderInfo.occType === '多种' ? orderInfo?.lastExtension?.occType : null, // v2.24 协助吴玉林修复历史问题“occType=多种”新增的拓展字段
    },
    rentRoomInfos,
    peripheralCount, // 外设数量
  };
};

export const formatToSubmitData: (data: ReducerState) => { [key: string]: unknown } = (data) => {
  const { commonInfo, rentRoomInfos, channelInfo, roomPriceList, storeRemarks } = data;
  const originRoomInfo = findItemByKey(rentRoomInfos, 'itemRole', ExchangeRoomRole.OriginRoom); // 房房间信息
  const targetRoomInfo = findItemByKey(rentRoomInfos, 'itemRole', ExchangeRoomRole.TargetRoom); // 新房间信息
  const priceStrategyId = channelInfo.strategyId;
  const { cachePriceMap } = flatPriceList(roomPriceList);
  const { roomTypeId, roomTypeName, roomId, roomNo, deposit } = targetRoomInfo;
  const strategyPrice = cachePriceMap[targetRoomInfo.roomTypeId] || {}; // 策略价格
  return {
    changePriceType: commonInfo.exchangeType === RoomExchangeType.KeepSame ? 0 : 1,
    destRoomId: roomId,
    destRoomNo: roomNo,
    destRoomTypeId: roomTypeId,
    destRoomTypeName: roomTypeName,
    recNo: commonInfo.recNo,
    deposit: parseYuan2Fen(deposit),
    roomChangeDayItemVO: (targetRoomInfo.roomPriceList || []).map((dayInfo) => {
      const {
        rentDate,
        inputPrice,
        couponAmount,
        couponName,
        couponNo,
        couponType,
        couponWyuid,
        couponSubType,
        extensionId,
      } = dayInfo;
      return {
        businessDate: format2TimeStamp(rentDate),
        priceStrategyId,
        strategyPrice: parseYuan2Fen(strategyPrice[rentDate]),
        price: parseYuan2Fen(inputPrice),
        couponAmount,
        couponName,
        couponNo,
        couponType,
        couponWyuid,
        couponSubType,
        extensionId,
      };
    }),
    srcRoomId: originRoomInfo.roomId,
    srcRoomName: originRoomInfo.roomNo,
    srcRoomTypeId: originRoomInfo.roomTypeId,
    srcRoomTypeName: originRoomInfo.roomTypeName,
    ...(storeRemarks || {}),
  };
};

// 定位节点
export const getPopupContainer = createTargetGetter(TargetType.CheckInExchange);
